import React, { useCallback } from "react";
import { z as zod } from "zod";
import isEmpty from "lodash/isEmpty";
import { UseFormReturn, useWatch } from "react-hook-form";

import dayjs from "@onair/utils/dayjs";

import {
	Box,
	Stack,
	Alert,
	Button,
	Typography,
	IconButton,
	CardContent,
	DialogTitle,
	Divider,
	Dialog,
	DialogContent,
	DialogActions,
} from "@mui/material";

import { schemaHelper } from "@onair/minimal-ui/hook-form";
import { useResponsive } from "@onair/minimal-ui/hooks/use-responsive";
import { Iconify } from "@onair/minimal-ui/iconify";
import { WizardSchemaType } from "./index.tsx";
import PreviewResource from "./preview/resource.tsx";
import StepResourceDesktop from "./step-resource-desktop.tsx";
import { AgendaResource } from "./resources/agendas/index.tsx";
import { DocResource } from "./resources/docs/index.tsx";
import { WhyChooseResource } from "./resources/why-chooses/index.tsx";
import { ObjectResource } from "./resources/objects/index.tsx";
import { FAQResource } from "./resources/faqs/index.tsx";
import CardWizard from "./card-wizard.tsx";

interface PropTypes {
	methods: UseFormReturn<WizardSchemaType>;
	navigator?: React.ReactNode;
}

export enum ResourceKey {
	AGENDAS = "agendas",
	DOCS = "docs",
	WHY = "why",
	OBJECT = "object",
	QAS = "qas",
}

export const StepResourceSchema = zod
	.object({
		agendas: zod.array(
			zod
				.object({
					startDate: schemaHelper
						.date({
							message: {
								required_error:
									"Vui lòng nhập thời gian bắt đầu của lịch trình",
							},
						})
						.refine((data) => dayjs().isBefore(data), {
							message: "Thời gian bắt đầu phải lớn hơn hiện tại",
						}),
					endDate: schemaHelper
						.date({
							message: {
								required_error:
									"Vui lòng nhập thời gian kết thúc của lịch trình",
							},
						})
						.refine((data) => dayjs().isBefore(data), {
							message: "Thời gian bắt đầu phải lớn hơn hiện tại",
						}),
					thumbnail: zod.custom<File | string | null>().optional(),
					title: zod.string().min(1, { message: "Nhập tiêu đề của sự kiện" }),
					content: zod.string().optional(),
				})
				.refine(
					(data) => {
						return (
							!data.endDate ||
							!data.startDate ||
							dayjs(data.endDate).isAfter(data.startDate)
						);
					},
					{
						message: "Thời gian kết thúc phải lớn hơn thời gian bắt đầu",
						path: ["endDate"],
					},
				),
		),
		docs: schemaHelper.files({ minFiles: 0 }).optional(),
		whyChooses: zod
			.array(zod.string().optional())
			.optional()
			.refine(
				(data) => {
					return data?.length === 0 || data?.[0];
				},
				{
					message: "Tạo ít nhất 2 lợi ích và tối đa 4.",
					path: ["0"],
				},
			)
			.refine(
				(data) => {
					return data?.length === 0 || data?.[1];
				},
				{
					message: "Tạo ít nhất 2 lợi ích và tối đa 4.",
					path: ["1"],
				},
			),
		objects: zod
			.array(zod.string().optional())
			.optional()
			.refine(
				(data) => {
					return data?.length === 0 || data?.[0];
				},
				{
					message: "Tạo ít nhất 2 đối tượng và tối đa 4.",
					path: ["0"],
				},
			)
			.refine(
				(data) => {
					return data?.length === 0 || data?.[1];
				},
				{
					message: "Tạo ít nhất 2 đối tượng và tối đa 4.",
					path: ["1"],
				},
			),
		qas: zod.array(
			zod.object({
				question: zod.string().min(1, { message: "Nhập câu hỏi" }),
				answer: zod.string().min(1, { message: "Nhập câu trả lời" }),
			}),
		),
	})
	.optional();

const StepResource: React.FC<PropTypes> = ({ methods, navigator }) => {
	const [showAgendas, setShowAgendas] = React.useState<boolean>(false);
	const [showDocs, setShowDocs] = React.useState<boolean>(false);
	const [showWhy, setShowWhy] = React.useState<boolean>(false);
	const [showObject, setShowObject] = React.useState<boolean>(false);
	const [showQAs, setShowQAs] = React.useState<boolean>(false);

	const [showAgendasSection, setShowAgendasSection] =
		React.useState<boolean>(false);
	const [showDocsSection, setShowDocsSection] = React.useState<boolean>(false);
	const [showWhySection, setShowWhySection] = React.useState<boolean>(false);
	const [showObjectSection, setShowObjectSection] =
		React.useState<boolean>(false);
	const [showQAsSection, setShowQAsSection] = React.useState<boolean>(false);

	const [warningModal, setWarningModal] = React.useState<{
		open: boolean;
		key: ResourceKey;
	} | null>(null);

	const { control, resetField, setValue, unregister } = methods;

	const handleToggleSection = useCallback((key: ResourceKey) => {
		switch (key) {
			case ResourceKey.AGENDAS:
				setShowAgendasSection((v) => !v);
				break;
			case ResourceKey.DOCS:
				setShowDocsSection((v) => !v);
				break;
			case ResourceKey.WHY:
				setShowWhySection((v) => !v);
				break;
			case ResourceKey.OBJECT:
				setShowObjectSection((v) => !v);
				break;
			case ResourceKey.QAS:
				setShowQAsSection((v) => !v);
				break;
			default:
				break;
		}
	}, []);

	const handleCloseWarningModal = useCallback(() => {
		setWarningModal(null);
	}, []);

	const handleOpenWarningModal = useCallback((key: ResourceKey) => {
		setWarningModal({
			open: true,
			key,
		});
	}, []);

	const handleDeleteResourceSection = useCallback(() => {
		const key = warningModal?.key;
		if (!key) return;
		switch (key) {
			case ResourceKey.AGENDAS:
				setShowAgendas(false);
				setShowAgendasSection(false);
				unregister("stepResource.agendas");
				setValue("stepResource.agendas", []);
				break;
			case ResourceKey.DOCS:
				setShowDocs(false);
				setShowDocsSection(false);
				unregister("stepResource.docs");
				setValue("stepResource.docs", []);
				break;
			case ResourceKey.WHY:
				setShowWhy(false);
				setShowWhySection(false);
				unregister("stepResource.whyChooses");
				setValue("stepResource.whyChooses", []);
				break;
			case ResourceKey.OBJECT:
				setShowObject(false);
				setShowObjectSection(false);
				unregister("stepResource.objects");
				setValue("stepResource.objects", []);
				break;
			case ResourceKey.QAS:
				setShowQAs(false);
				setShowQAsSection(false);
				unregister("stepResource.qas");
				setValue("stepResource.qas", []);
				break;
			default:
				break;
		}
		handleCloseWarningModal();
	}, [warningModal, resetField, handleCloseWarningModal]);

	const mdDown = useResponsive("down", "md");

	const data = useWatch({
		control,
	});

	React.useEffect(() => {
		if (!isEmpty(data.stepResource?.docs)) {
			setShowDocs(true);
		}
		if (!isEmpty(data.stepResource?.whyChooses)) {
			setShowWhy(true);
		}
		if (!isEmpty(data.stepResource?.objects)) {
			setShowObject(true);
		}
		if (data.stepResource?.qas && data.stepResource?.qas?.length > 0) {
			setShowQAs(true);
		}
		if (data.stepResource?.agendas && data.stepResource?.agendas?.length > 0) {
			setShowAgendas(true);
		}
	}, [
		data.stepResource?.docs,
		data.stepResource?.objects,
		data.stepResource?.qas,
		data.stepResource?.whyChooses,
		data.stepResource?.agendas,
	]);

	const handleToggle = useCallback(
		(key: ResourceKey) => {
			switch (key) {
				case ResourceKey.AGENDAS:
					if (showAgendas) {
						handleOpenWarningModal(ResourceKey.AGENDAS);
						break;
					}
					if (!showAgendasSection) {
						setValue("stepResource.agendas", [
							{
								title: "",
								startDate: null,
								endDate: null,
							},
						]);
					}
					handleToggleSection(ResourceKey.AGENDAS);
					setShowAgendas((v) => !v);
					break;
				case ResourceKey.DOCS:
					if (showDocs) {
						handleOpenWarningModal(ResourceKey.DOCS);
						break;
					}
					handleToggleSection(ResourceKey.DOCS);
					setShowDocs((v) => !v);
					break;
				case ResourceKey.WHY:
					if (showWhy) {
						handleOpenWarningModal(ResourceKey.WHY);
						break;
					}
					handleToggleSection(ResourceKey.WHY);
					setShowWhy((v) => !v);
					break;
				case ResourceKey.OBJECT:
					if (showObject) {
						handleOpenWarningModal(ResourceKey.OBJECT);
						break;
					}
					handleToggleSection(ResourceKey.OBJECT);
					setShowObject((v) => !v);
					break;
				case ResourceKey.QAS:
					if (showQAs) {
						handleOpenWarningModal(ResourceKey.QAS);
						break;
					}
					if (!showQAsSection) {
						setValue("stepResource.qas", [
							{
								question: "",
								answer: "",
							},
						]);
					}
					handleToggleSection(ResourceKey.QAS);
					setShowQAs((v) => !v);
					break;
				default:
					break;
			}
		},
		[
			showAgendas,
			showAgendasSection,
			showDocs,
			showWhy,
			showObject,
			showQAs,
			handleOpenWarningModal,
			handleToggleSection,
			setValue,
		],
	);

	const toggledAll = React.useMemo(() => {
		return showAgendas && showDocs && showWhy && showObject && showQAs;
	}, [showAgendas, showDocs, showWhy, showObject, showQAs]);

	const { labelWarning } = React.useMemo(() => {
		if (!warningModal?.open) {
			return {
				labelWarning: "",
			};
		}

		switch (warningModal.key) {
			case ResourceKey.AGENDAS:
				return {
					labelWarning: "Agenda",
				};
			case ResourceKey.DOCS:
				return {
					labelWarning: "Tài liệu",
				};
			case ResourceKey.WHY:
				return {
					labelWarning: "Tại sao nên tham gia sự kiện",
				};
			case ResourceKey.OBJECT:
				return {
					labelWarning: "Sự kiện dành cho ai",
				};
			case ResourceKey.QAS:
				return {
					labelWarning: "Câu hỏi thường gặp",
				};
			default:
				return {
					labelWarning: "",
				};
		}
	}, [warningModal]);

	return (
		<Stack direction="row" spacing={3}>
			<Box flex={1}>
				<Stack spacing={2}>
					{!mdDown && (
						<StepResourceDesktop
							methods={methods}
							onToggle={handleToggle}
							onExpandSection={handleToggleSection}
							showAgendas={showAgendas}
							showDocs={showDocs}
							showWhy={showWhy}
							showObject={showObject}
							showQAs={showQAs}
							expandAgendasSection={showAgendasSection}
							expandDocsSection={showDocsSection}
							expandWhySection={showWhySection}
							expandObjectSection={showObjectSection}
							expandQAsSection={showQAsSection}
						/>
					)}

					{(!toggledAll || mdDown) && (
						<CardWizard>
							<CardContent>
								<Stack spacing={3}>
									<Alert variant="outlined" severity="info">
										Bạn có thể tiếp tục mà không cần thêm thông tin này, nhưng
										nếu bổ sung, sự kiện sẽ dễ thu hút hơn và tăng cơ hội bán vé
										thành công.
									</Alert>
									<Stack spacing={2}>
										<Typography fontWeight={700} variant="h6" flex={1}>
											Tài nguyên
										</Typography>
										{(!showAgendas || mdDown) && (
											<Stack
												direction="row"
												justifyContent="space-between"
												alignItems="center"
											>
												<AgendaResource.Header />
												{mdDown && (
													<Dialog
														fullScreen={true}
														open={showAgendasSection}
														onClose={() =>
															handleToggleSection(ResourceKey.AGENDAS)
														}
													>
														<DialogTitle
															sx={{
																py: 2,
																px: 1,
															}}
														>
															<Stack direction="row" alignItems="center">
																<IconButton
																	onClick={() =>
																		handleToggleSection(ResourceKey.AGENDAS)
																	}
																	size="medium"
																>
																	<Iconify
																		width={24}
																		icon="eva:arrow-ios-back-fill"
																	/>
																</IconButton>
																<AgendaResource.Header showIcon={false} />
															</Stack>
														</DialogTitle>
														<Divider />
														<DialogContent
															sx={{
																p: 2,
																pb: 3,
															}}
														>
															<AgendaResource.Content methods={methods} />
														</DialogContent>
													</Dialog>
												)}
												{showAgendas ? (
													<Stack direction="row" mr={1} spacing={1}>
														<Button
															variant="soft"
															sx={{
																width: 36,
																minWidth: "unset",
															}}
															onClick={() => {
																handleToggle(ResourceKey.AGENDAS);
															}}
														>
															<Iconify
																color="action.active"
																width={18}
																icon="solar:trash-bin-trash-bold"
															/>
														</Button>
														<Button
															variant="soft"
															sx={{
																width: 34,
																minWidth: "unset",
															}}
															onClick={() =>
																handleToggleSection(ResourceKey.AGENDAS)
															}
														>
															<Iconify
																color="action.active"
																width={18}
																icon="eva:arrow-ios-downward-fill"
															/>
														</Button>
													</Stack>
												) : (
													<Button
														variant="soft"
														color="primary"
														onClick={() => handleToggle(ResourceKey.AGENDAS)}
													>
														Thêm
													</Button>
												)}
											</Stack>
										)}
										{(!showDocs || mdDown) && (
											<Stack
												direction="row"
												spacing={2}
												justifyContent="space-between"
												alignItems="center"
											>
												<DocResource.Header />
												{mdDown && (
													<Dialog
														fullScreen={true}
														open={showDocsSection}
														onClose={() =>
															handleToggleSection(ResourceKey.DOCS)
														}
													>
														<DialogTitle
															sx={{
																py: 2,
																px: 1,
															}}
														>
															<Stack direction="row" alignItems="center">
																<IconButton
																	onClick={() =>
																		handleToggleSection(ResourceKey.DOCS)
																	}
																	size="medium"
																>
																	<Iconify
																		width={24}
																		icon="eva:arrow-ios-back-fill"
																	/>
																</IconButton>
																<DocResource.Header showIcon={false} />
															</Stack>
														</DialogTitle>
														<Divider />
														<DialogContent
															sx={{
																p: 2,
																pb: 3,
															}}
														>
															<DocResource.Content methods={methods} />
														</DialogContent>
													</Dialog>
												)}
												{showDocs ? (
													<Stack direction="row" mr={1} spacing={1}>
														<Button
															variant="soft"
															sx={{
																width: 36,
																minWidth: "unset",
															}}
															onClick={() => {
																handleToggle(ResourceKey.DOCS);
															}}
														>
															<Iconify
																color="action.active"
																width={18}
																icon="solar:trash-bin-trash-bold"
															/>
														</Button>
														<Button
															variant="soft"
															sx={{
																width: 34,
																minWidth: "unset",
															}}
															onClick={() =>
																handleToggleSection(ResourceKey.DOCS)
															}
														>
															<Iconify
																color="action.active"
																width={18}
																icon="eva:arrow-ios-downward-fill"
															/>
														</Button>
													</Stack>
												) : (
													<Button
														variant="soft"
														color="primary"
														disabled={showDocs}
														onClick={() => handleToggle(ResourceKey.DOCS)}
													>
														Thêm
													</Button>
												)}
											</Stack>
										)}
										{(!showWhy || mdDown) && (
											<Stack
												direction="row"
												spacing={2}
												justifyContent="space-between"
												alignItems="center"
											>
												<WhyChooseResource.Header />
												{mdDown && (
													<Dialog
														fullScreen={true}
														open={showWhySection}
														onClose={() => handleToggleSection(ResourceKey.WHY)}
													>
														<DialogTitle
															sx={{
																py: 2,
																px: 1,
															}}
														>
															<Stack direction="row" alignItems="center">
																<IconButton
																	onClick={() =>
																		handleToggleSection(ResourceKey.WHY)
																	}
																	size="medium"
																>
																	<Iconify
																		width={24}
																		icon="eva:arrow-ios-back-fill"
																	/>
																</IconButton>
																<WhyChooseResource.Header showIcon={false} />
															</Stack>
														</DialogTitle>
														<Divider />
														<DialogContent
															sx={{
																p: 2,
																pb: 3,
															}}
														>
															<WhyChooseResource.Content />
														</DialogContent>
													</Dialog>
												)}
												{showWhy ? (
													<Stack direction="row" mr={1} spacing={1}>
														<Button
															variant="soft"
															sx={{
																width: 36,
																minWidth: "unset",
															}}
															onClick={() => {
																handleToggle(ResourceKey.WHY);
															}}
														>
															<Iconify
																color="action.active"
																width={18}
																icon="solar:trash-bin-trash-bold"
															/>
														</Button>
														<Button
															variant="soft"
															sx={{
																width: 34,
																minWidth: "unset",
															}}
															onClick={() =>
																handleToggleSection(ResourceKey.WHY)
															}
														>
															<Iconify
																color="action.active"
																width={18}
																icon="eva:arrow-ios-downward-fill"
															/>
														</Button>
													</Stack>
												) : (
													<Button
														variant="soft"
														color="primary"
														onClick={() => handleToggle(ResourceKey.WHY)}
													>
														Thêm
													</Button>
												)}
											</Stack>
										)}
										{(!showObject || mdDown) && (
											<Stack
												direction="row"
												spacing={2}
												justifyContent="space-between"
												alignItems="center"
											>
												<ObjectResource.Header />
												{mdDown && (
													<Dialog
														fullScreen={true}
														open={showObjectSection}
														onClose={() =>
															handleToggleSection(ResourceKey.OBJECT)
														}
													>
														<DialogTitle
															sx={{
																py: 2,
																px: 1,
															}}
														>
															<Stack direction="row" alignItems="center">
																<IconButton
																	onClick={() =>
																		handleToggleSection(ResourceKey.OBJECT)
																	}
																	size="medium"
																>
																	<Iconify
																		width={24}
																		icon="eva:arrow-ios-back-fill"
																	/>
																</IconButton>
																<ObjectResource.Header showIcon={false} />
															</Stack>
														</DialogTitle>
														<Divider />
														<DialogContent
															sx={{
																p: 2,
																pb: 3,
															}}
														>
															<ObjectResource.Content />
														</DialogContent>
													</Dialog>
												)}
												{showObject ? (
													<Stack direction="row" mr={1} spacing={1}>
														<Button
															variant="soft"
															sx={{
																width: 36,
																minWidth: "unset",
															}}
															onClick={() => {
																handleToggle(ResourceKey.OBJECT);
															}}
														>
															<Iconify
																color="action.active"
																width={18}
																icon="solar:trash-bin-trash-bold"
															/>
														</Button>
														<Button
															variant="soft"
															sx={{
																width: 34,
																minWidth: "unset",
															}}
															onClick={() =>
																handleToggleSection(ResourceKey.OBJECT)
															}
														>
															<Iconify
																color="action.active"
																width={18}
																icon="eva:arrow-ios-downward-fill"
															/>
														</Button>
													</Stack>
												) : (
													<Button
														variant="soft"
														color="primary"
														onClick={() => handleToggle(ResourceKey.OBJECT)}
													>
														Thêm
													</Button>
												)}
											</Stack>
										)}
										{(!showQAs || mdDown) && (
											<Stack
												direction="row"
												spacing={2}
												justifyContent="space-between"
												alignItems="center"
											>
												<FAQResource.Header />
												{mdDown && (
													<Dialog
														fullScreen={true}
														open={showQAsSection}
														onClose={() => handleToggleSection(ResourceKey.QAS)}
													>
														<DialogTitle
															sx={{
																py: 2,
																px: 1,
															}}
														>
															<Stack direction="row" alignItems="center">
																<IconButton
																	onClick={() =>
																		handleToggleSection(ResourceKey.QAS)
																	}
																	size="medium"
																>
																	<Iconify
																		width={24}
																		icon="eva:arrow-ios-back-fill"
																	/>
																</IconButton>
																<FAQResource.Header showIcon={false} />
															</Stack>
														</DialogTitle>
														<Divider />
														<DialogContent
															sx={{
																p: 2,
																pb: 3,
															}}
														>
															<FAQResource.Content methods={methods} />
														</DialogContent>
													</Dialog>
												)}
												{showQAs ? (
													<Stack direction="row" mr={1} spacing={1}>
														<Button
															variant="soft"
															sx={{
																width: 36,
																minWidth: "unset",
															}}
															onClick={() => {
																handleToggle(ResourceKey.QAS);
															}}
														>
															<Iconify
																color="action.active"
																width={18}
																icon="solar:trash-bin-trash-bold"
															/>
														</Button>
														<Button
															variant="soft"
															sx={{
																width: 34,
																minWidth: "unset",
															}}
															onClick={() =>
																handleToggleSection(ResourceKey.QAS)
															}
														>
															<Iconify
																color="action.active"
																width={18}
																icon="eva:arrow-ios-downward-fill"
															/>
														</Button>
													</Stack>
												) : (
													<Button
														variant="soft"
														color="primary"
														onClick={() => handleToggle(ResourceKey.QAS)}
													>
														Thêm
													</Button>
												)}
											</Stack>
										)}
									</Stack>
								</Stack>
							</CardContent>
						</CardWizard>
					)}

					<Dialog
						open={warningModal?.open || false}
						maxWidth="xs"
						fullWidth
						scroll="paper"
					>
						{labelWarning && (
							<>
								<DialogTitle
									display="flex"
									sx={{
										p: 3,
										pr: 2,
										justifyContent: "space-between",
										alignItems: "center",
									}}
								>
									<Typography fontWeight={700} variant="h6">
										Xoá “{labelWarning}”?
									</Typography>
									<IconButton
										aria-label="close"
										onClick={handleCloseWarningModal}
									>
										<Iconify
											sx={{
												color: "action.active",
											}}
											icon="eva:close-fill"
											width={18}
										/>
									</IconButton>
								</DialogTitle>
								<Box px={3}>
									<Typography>
										Bạn có chắc chắn muốn xoá <strong>{labelWarning}</strong>{" "}
										không?
									</Typography>
								</Box>
								<DialogActions>
									<Button
										onClick={handleCloseWarningModal}
										variant="outlined"
										color="inherit"
										size="medium"
									>
										Tiếp tục chỉnh sửa
									</Button>
									<Button
										variant="contained"
										color="error"
										size="medium"
										onClick={() => {
											handleDeleteResourceSection();
										}}
									>
										Xóa
									</Button>
								</DialogActions>
							</>
						)}
					</Dialog>
				</Stack>
				{navigator}
			</Box>
			{!mdDown && <PreviewResource data={data as WizardSchemaType} />}
		</Stack>
	);
};

export default StepResource;
